import { useEffect } from 'react'

/**
 * The tags a page can set without a `<head>` templating library.
 *
 * <p>This app has no react-helmet and no SSR: every route serves the same
 * static `index.html`. A build-time prerender step reads whatever a page's
 * own `useDocumentHead()` call has written into `document` after it settles,
 * so every marketing/blog page must set its own title/meta/JSON-LD at
 * runtime for that step to see anything page-specific.
 */
export interface DocumentHeadInput {
  /** Sets `document.title`. */
  title: string
  /** `<meta name="description">`. */
  description: string
  /** `<link rel="canonical">`, and the default for `og:url`/`twitter:url`. */
  canonical: string
  /** `og:title` / `twitter:title`. Falls back to `title`. */
  ogTitle?: string
  /** `og:description` / `twitter:description`. Falls back to `description`. */
  ogDescription?: string
  /** `og:image` / `twitter:image`, when the page has a share image. */
  ogImage?: string
  /**
   * One schema.org object, or several, embedded as `<script
   * type="application/ld+json">` tags. Replaces whatever this hook last
   * inserted, so navigating between two pages never leaves the previous
   * page's structured data behind.
   */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

const MANAGED_ATTR = 'data-managed-by'
const MANAGED_VALUE = 'useDocumentHead'

/** Creates the tag if missing, otherwise updates it in place. */
function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function upsertCanonical(href: string): void {
  let tag = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!tag) {
    tag = document.createElement('link')
    tag.setAttribute('rel', 'canonical')
    document.head.appendChild(tag)
  }
  tag.setAttribute('href', href)
}

/**
 * Escapes `<` as `<` so a literal `</script>` inside a serialized
 * string (a blog post title or description pulled from a CMS this app does
 * not control) can never break out of the script tag it is embedded in.
 */
function safeJsonLd(value: Record<string, unknown>): string {
  return JSON.stringify(value).replace(/</g, '\\u003c')
}

function clearManagedJsonLd(): void {
  document
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTR}="${MANAGED_VALUE}"]`)
    .forEach((node) => node.remove())
}

/**
 * Sets this page's title, meta description, canonical link, Open Graph and
 * Twitter card tags, and (optionally) JSON-LD structured data.
 *
 * <p>Call once per page component. Re-runs whenever the input values change,
 * so client-side navigation between two pages of the same shape (e.g. two
 * blog posts) keeps the tags in sync. Deliberately does not reset
 * title/meta on unmount — the next page's own call overwrites them — but
 * does clean up JSON-LD on unmount/change, since a page with no `jsonLd`
 * would otherwise inherit the previous page's structured data.
 */
export function useDocumentHead(input: DocumentHeadInput): void {
  const { title, description, canonical, ogTitle, ogDescription, ogImage, jsonLd } = input

  useEffect(() => {
    document.title = title
    upsertMeta('name', 'description', description)
    upsertCanonical(canonical)

    upsertMeta('property', 'og:title', ogTitle ?? title)
    upsertMeta('property', 'og:description', ogDescription ?? description)
    upsertMeta('property', 'og:url', canonical)

    upsertMeta('name', 'twitter:title', ogTitle ?? title)
    upsertMeta('name', 'twitter:description', ogDescription ?? description)

    if (ogImage) {
      upsertMeta('property', 'og:image', ogImage)
      upsertMeta('name', 'twitter:image', ogImage)
      upsertMeta('name', 'twitter:card', 'summary_large_image')
    } else {
      upsertMeta('name', 'twitter:card', 'summary')
    }

    clearManagedJsonLd()
    if (jsonLd) {
      const entries = Array.isArray(jsonLd) ? jsonLd : [jsonLd]
      entries.forEach((entry) => {
        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.setAttribute(MANAGED_ATTR, MANAGED_VALUE)
        script.textContent = safeJsonLd(entry)
        document.head.appendChild(script)
      })
    }

    return () => {
      clearManagedJsonLd()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, jsonLd])
}
